import { bcInt } from '@lib2/model/util/string/BaseConverter';
import { MediaStateTypeId } from '@lib2/model/media/MediaStateTypeObj';
import { ListlistTypeId } from '@lib2/model/listlist/ListlistType';
import type { ListlistDto, ListlistRow, ListlistOrderedNode } from '@lib2/model/listlist/ListlistDto';
import type { ListlistCreateReq, ListlistUpdateReq } from '@lib2/model/listlist/ListlistReq';

// ── Display model for content tables ──

export interface ContentTableRow {
  llId: number;
  displayId: string;
  title: string;
  isActive: boolean;
  childCount: number;
  created: number;
}

// ── Form data for settings forms ──

export interface ListlistFormData {
  title: string;
  descShort: string;
  mediaStateTypeId: number;
}

export class AppContentUtils {
  // ── ID Conversions (llId uses bcInt) ──

  static getDisplayId(llId: number): string {
    return bcInt.getStr(llId);
  }

  static getNumericId(displayId: string): number {
    return bcInt.getNum(displayId);
  }

  // ── Table row builder ──

  static buildTableRow(
    dto: ListlistDto,
    childCount: number,
  ): ContentTableRow {
    return {
      llId: dto.llr.llId,
      displayId: AppContentUtils.getDisplayId(dto.llr.llId),
      title: dto.llr.title,
      isActive: dto.llr.mediaStateTypeId === MediaStateTypeId.Active,
      childCount,
      created: dto.llr.created,
    };
  }

  // ── DTO → Form data ──

  static transformToFormData(llr: ListlistRow): ListlistFormData {
    return {
      title: llr.title,
      descShort: llr.descShort ?? '',
      mediaStateTypeId: llr.mediaStateTypeId,
    };
  }

  // ── Form data → Create request ──

  static buildCreateReq(
    title: string,
    typeId: ListlistTypeId,
  ): ListlistCreateReq {
    return {
      listlistTypeId: typeId,
      mediaStateTypeId: MediaStateTypeId.Active,
      title,
      descShort: null,
    };
  }

  // ── Form data → Update request ──

  static buildUpdateReq(form: ListlistFormData): ListlistUpdateReq {
    return {
      mediaStateTypeId: form.mediaStateTypeId,
      title: form.title,
      descShort: form.descShort || null,
    };
  }

  // ── Child count from positions graph ──

  /**
   * Build a map of llId → direct child count from a positions tree.
   * Counts childListLists (listlists) + childListIds (playlists/lists).
   */
  static buildChildCountMap(
    rootNodes: ListlistOrderedNode[],
  ): Map<number, number> {
    const map = new Map<number, number>();

    function walk(node: ListlistOrderedNode) {
      map.set(node.llId, node.childListLists.length + node.childListIds.length);
      for (const child of node.childListLists) {
        walk(child);
      }
    }

    for (const root of rootNodes) {
      walk(root);
    }
    return map;
  }

  /**
   * Get ordered child IDs for a specific parent from a positions tree.
   * Returns { llIds: number[], listIds: number[] } representing ordered children.
   */
  static getOrderedChildren(
    parentLlId: number,
    rootNodes: ListlistOrderedNode[],
  ): { llIds: number[]; listIds: number[] } | null {
    function find(node: ListlistOrderedNode): ListlistOrderedNode | null {
      if (node.llId === parentLlId) return node;
      for (const child of node.childListLists) {
        const found = find(child);
        if (found) return found;
      }
      return null;
    }

    for (const root of rootNodes) {
      const found = find(root);
      if (found) {
        return {
          llIds: found.childListLists.map((c) => c.llId),
          listIds: found.childListIds,
        };
      }
    }
    return null;
  }

  // ── Child type label helpers ──

  static getChildTypeLabel(parentType: ListlistTypeId): string {
    switch (parentType) {
      case ListlistTypeId.Root:
        return 'Category';
      case ListlistTypeId.Category:
        return 'Series';
      case ListlistTypeId.Series:
        return 'Season';
      default:
        return 'Item';
    }
  }

  static getChildTypeLabelPlural(parentType: ListlistTypeId): string {
    switch (parentType) {
      case ListlistTypeId.Root:
        return 'Categories';
      case ListlistTypeId.Category:
        return 'Series';
      case ListlistTypeId.Series:
        return 'Seasons';
      default:
        return 'Items';
    }
  }
}
